import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Trash2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminCommunity = ({ posts, users, onRefresh }) => {
  const { token } = useAuth();
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('posts');
  const [searchTerm, setSearchTerm] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null); // { type: 'post', id: '...' }

  const handleDeletePost = async (postId) => {
    try {
      await axios.delete(`${API}/posts/${postId}`, { 
        headers: { Authorization: `Bearer ${token}` } 
      });
      toast.success(t('admin.postDeleted', '帖子已删除'));
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('common.error', '操作失败'));
    }
    setDeleteTarget(null);
  };

  const filteredPosts = posts.filter(post => 
    post.content?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    post.user_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    post.tags?.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const filteredUsers = users.filter(user =>
    user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div data-testid="admin-community">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">{t('admin.community', '社区管理')}</h1>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6">
        <Button variant={activeTab === 'posts' ? 'default' : 'outline'} onClick={() => setActiveTab('posts')}>
          {t('admin.posts', '帖子')} ({posts.length})
        </Button>
        <Button variant={activeTab === 'users' ? 'default' : 'outline'} onClick={() => setActiveTab('users')}>
          {t('admin.users', '用户')} ({users.length})
        </Button>
      </div>

      {/* Search */}
      <div className="mb-4">
        <Input 
          placeholder={t('admin.searchCommunity', '搜索帖子或用户...')} 
          value={searchTerm} 
          onChange={(e) => setSearchTerm(e.target.value)}
          className="max-w-md"
        />
      </div>

      {/* Posts Tab */}
      {activeTab === 'posts' && (
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('admin.author', '作者')}</TableHead>
                <TableHead>{t('admin.content', '内容')}</TableHead>
                <TableHead>{t('admin.tags', '标签')}</TableHead>
                <TableHead>{t('admin.stats', '数据')}</TableHead>
                <TableHead>{t('admin.date', '日期')}</TableHead>
                <TableHead>{t('admin.actions', '操作')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredPosts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-gray-500 py-8">
                    {t('admin.noPosts', '暂无帖子')}
                  </TableCell>
                </TableRow>
              ) : (
                filteredPosts.map((post) => (
                  <TableRow key={post.id}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-gray-200 overflow-hidden">
                          {post.user_avatar && (
                            <img src={post.user_avatar} alt="" className="w-full h-full object-cover" />
                          )}
                        </div>
                        <span className="text-sm font-medium">{post.user_name}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <p className="text-sm text-gray-600 line-clamp-2 max-w-[300px]">
                        {post.content}
                      </p>
                      {post.images?.length > 0 && (
                        <span className="text-xs text-gray-400">📷 {post.images.length}张图片</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {post.tags?.slice(0, 3).map((tag) => (
                          <Badge key={tag} variant="outline" className="text-xs">{tag}</Badge>
                        ))}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="text-xs text-gray-500">
                        <div>❤️ {post.likes_count || 0}</div>
                        <div>💬 {post.comments_count || 0}</div>
                      </div>
                    </TableCell>
                    <TableCell className="text-sm text-gray-500">
                      {new Date(post.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => setDeleteTarget({ type: 'post', id: post.id, name: post.content?.substring(0, 30) })}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </Card>
      )}

      {/* Users Tab */}
      {activeTab === 'users' && (
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('admin.user', '用户')}</TableHead>
                <TableHead>{t('admin.email', '邮箱')}</TableHead>
                <TableHead>{t('admin.role', '角色')}</TableHead>
                <TableHead>{t('admin.followers', '粉丝')}</TableHead>
                <TableHead>{t('admin.registerDate', '注册日期')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredUsers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center text-gray-500 py-8">
                    {t('admin.noUsers', '暂无用户')}
                  </TableCell>
                </TableRow>
              ) : (
                filteredUsers.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-gray-200 overflow-hidden">
                          {user.avatar && (
                            <img src={user.avatar} alt="" className="w-full h-full object-cover" />
                          )}
                        </div>
                        <span className="text-sm font-medium">{user.name}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-sm">{user.email}</TableCell>
                    <TableCell>
                      <Badge variant={user.role === 'admin' ? 'default' : 'outline'}>
                        {user.role === 'admin' ? '管理员' : '普通用户'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm">{user.followers_count || 0}</TableCell>
                    <TableCell className="text-sm text-gray-500">
                      {new Date(user.created_at).toLocaleDateString()}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </Card>
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('admin.confirmDelete', '确认删除')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('admin.confirmDeletePost', '确定要删除这篇帖子吗？此操作无法撤销。')}
              {deleteTarget?.name && (
                <p className="mt-2 text-gray-600 text-sm">"{deleteTarget.name}..."</p>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel', '取消')}</AlertDialogCancel>
            <AlertDialogAction 
              onClick={() => handleDeletePost(deleteTarget?.id)}
              className="bg-red-500 hover:bg-red-600"
            >
              {t('common.delete', '删除')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminCommunity;
